<script>
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import TelegramApi from 'dashboard/api/telegramApi';

const SINCE_OPTIONS = [
  { value: 30, key: '30' },
  { value: 90, key: '90' },
  { value: 180, key: '180' },
  { value: 365, key: '365' },
];

export default {
  components: { SettingsFieldSection, NextButton, SpinnerLoader },
  props: {
    inbox: { type: Object, required: true },
  },
  data() {
    return {
      availableChats: [],
      selectedChatIds: [],
      isLoadingChats: false,
      isSavingMonitored: false,
      expandedChatId: null,
      chatMembers: {}, // chatId -> [members]
      loadingMembersChatId: null,
      // Support is marked globally across all monitored groups: one user
      // flagged as support anywhere counts everywhere. The UI maintains the
      // flat Set here; on save we write the same list under every chat_id
      // key in the backend payload.
      globalSupportIds: new Set(),
      isSavingSupport: false,
      sinceDays: 90,
      sinceOptions: SINCE_OPTIONS,
      isImporting: false,
      statuses: [],
      pollIntervalId: null,
      memberSearch: {},
    };
  },
  computed: {
    channelId() {
      return this.inbox.channel_id;
    },
    hasCaptainAssistant() {
      return !!this.inbox.captain_assistant_id;
    },
    selectedChatsWithoutSupport() {
      return this.globalSupportIds.size === 0 ? this.selectedChatIds : [];
    },
  },
  mounted() {
    this.bootstrap();
  },
  beforeUnmount() {
    if (this.pollIntervalId) clearInterval(this.pollIntervalId);
  },
  methods: {
    async bootstrap() {
      this.selectedChatIds = [...(this.inbox.telegram_api_monitored_chats || [])];
      const existing = this.inbox.telegram_api_support_user_ids_by_chat || {};
      const union = new Set();
      Object.values(existing).forEach(ids => (ids || []).forEach(id => union.add(id)));
      this.globalSupportIds = union;
      await this.loadChats();
      await this.refreshStatuses();
    },
    async loadChats() {
      this.isLoadingChats = true;
      try {
        const { data } = await TelegramApi.getChats(this.channelId);
        this.availableChats = (data.chats || []).filter(
          c => c.type !== 'chatTypePrivate'
        );
      } catch {
        useAlert(this.$t('INBOX_MGMT.GROUPS_IMPORT.LOAD_CHATS_ERROR'));
      } finally {
        this.isLoadingChats = false;
      }
    },
    toggleChat(chatId) {
      const idx = this.selectedChatIds.indexOf(chatId);
      if (idx > -1) this.selectedChatIds.splice(idx, 1);
      else this.selectedChatIds.push(chatId);
    },
    async saveMonitored() {
      this.isSavingMonitored = true;
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: { monitored_chats: this.selectedChatIds },
        });
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSavingMonitored = false;
      }
    },
    async toggleExpand(chatId) {
      this.expandedChatId = this.expandedChatId === chatId ? null : chatId;
      if (this.expandedChatId && !this.chatMembers[chatId]) {
        await this.loadMembers(chatId);
      }
    },
    async loadMembers(chatId) {
      this.loadingMembersChatId = chatId;
      try {
        await TelegramApi.fetchChatMembers({ channelId: this.channelId, chatId });
        const deadline = Date.now() + 15000;
        // eslint-disable-next-line no-await-in-loop
        while (Date.now() < deadline) {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await TelegramApi.getChatMembers({
            channelId: this.channelId,
            chatId,
          });
          if (data.ready) {
            this.chatMembers = { ...this.chatMembers, [chatId]: data.members || [] };
            (data.support_user_ids || []).forEach(id => this.globalSupportIds.add(id));
            this.globalSupportIds = new Set(this.globalSupportIds);
            return;
          }
          // eslint-disable-next-line no-await-in-loop
          await new Promise(r => setTimeout(r, 1000));
        }
        useAlert(this.$t('INBOX_MGMT.GROUPS_IMPORT.LOAD_MEMBERS_ERROR'));
      } catch {
        useAlert(this.$t('INBOX_MGMT.GROUPS_IMPORT.LOAD_MEMBERS_ERROR'));
      } finally {
        this.loadingMembersChatId = null;
      }
    },
    toggleSupport(_chatId, userId) {
      const next = new Set(this.globalSupportIds);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      this.globalSupportIds = next;
    },
    isSupport(_chatId, userId) {
      return this.globalSupportIds.has(userId);
    },
    filteredMembers(chatId) {
      const members = this.chatMembers[chatId] || [];
      const term = (this.memberSearch[chatId] || '').toLowerCase().trim();
      if (!term) return members;
      return members.filter(m => {
        const name = `${m.first_name || ''} ${m.last_name || ''} ${m.username || ''}`.toLowerCase();
        return name.includes(term);
      });
    },
    setMemberSearch(chatId, value) {
      this.memberSearch = { ...this.memberSearch, [chatId]: value };
    },
    async saveSupport() {
      this.isSavingSupport = true;
      try {
        // Apply the global set to every monitored chat so the backend
        // topic-cluster service sees the same support team in any chat.
        const ids = [...this.globalSupportIds];
        const payload = {};
        this.selectedChatIds.forEach(chatId => {
          payload[chatId] = ids;
        });
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel: { support_user_ids_by_chat: payload },
        });
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSavingSupport = false;
      }
    },
    async startImport() {
      if (!this.selectedChatIds.length) return;
      if (this.selectedChatsWithoutSupport.length) {
        const proceed = window.confirm(
          this.$t('INBOX_MGMT.GROUPS_IMPORT.NO_SUPPORT_WARNING')
        );
        if (!proceed) return;
      }
      this.isImporting = true;
      try {
        await TelegramApi.backfill({
          channelId: this.channelId,
          chatIds: this.selectedChatIds,
          sinceDays: this.sinceDays,
        });
        useAlert(this.$t('INBOX_MGMT.GROUPS_IMPORT.IMPORT_STARTED'));
        this.startPolling();
      } catch {
        useAlert(this.$t('INBOX_MGMT.GROUPS_IMPORT.IMPORT_ERROR'));
      } finally {
        this.isImporting = false;
      }
    },
    async refreshStatuses() {
      try {
        const { data } = await TelegramApi.getBackfillStatus(this.channelId);
        this.statuses = data.statuses || [];
      } catch {
        // keep silent; status endpoint is non-critical
      }
    },
    startPolling() {
      if (this.pollIntervalId) clearInterval(this.pollIntervalId);
      this.pollIntervalId = setInterval(() => {
        this.refreshStatuses();
        const allDone =
          this.statuses.length > 0 &&
          this.statuses.every(s => ['completed', 'idle', 'error'].includes(s.status));
        if (allDone) {
          clearInterval(this.pollIntervalId);
          this.pollIntervalId = null;
        }
      }, 2000);
    },
    chatTitle(chatId) {
      const chat = this.availableChats.find(c => c.id === chatId);
      return chat ? chat.title : `Chat ${chatId}`;
    },
    memberLabel(m) {
      const name = `${m.first_name || ''} ${m.last_name || ''}`.trim();
      const uname = m.username ? ` @${m.username}` : '';
      return `${name || `User ${m.id}`}${uname}`;
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Section 1: Monitored groups -->
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.GROUPS_IMPORT.MONITORED.TITLE')"
      :help-text="$t('INBOX_MGMT.GROUPS_IMPORT.MONITORED.HELP')"
    >
      <div class="flex flex-col gap-2">
        <SpinnerLoader v-if="isLoadingChats" />
        <div
          v-else-if="availableChats.length === 0"
          class="text-sm text-n-slate-11"
        >
          {{ $t('INBOX_MGMT.GROUPS_IMPORT.NO_CHATS') }}
        </div>
        <div
          v-for="chat in availableChats"
          v-else
          :key="chat.id"
          class="border border-n-slate-4 rounded p-2"
        >
          <div class="flex items-center gap-2">
            <input
              :id="`chat-${chat.id}`"
              type="checkbox"
              :checked="selectedChatIds.includes(chat.id)"
              @change="toggleChat(chat.id)"
            />
            <label
              :for="`chat-${chat.id}`"
              class="flex-1 text-sm text-n-slate-12"
            >
              {{ chat.title || $t('INBOX_MGMT.GROUPS_IMPORT.UNNAMED_CHAT') }}
              <span class="text-n-slate-10">({{ chat.type }})</span>
            </label>
            <NextButton
              v-if="selectedChatIds.includes(chat.id)"
              :label="
                expandedChatId === chat.id
                  ? $t('INBOX_MGMT.GROUPS_IMPORT.HIDE_MEMBERS')
                  : $t('INBOX_MGMT.GROUPS_IMPORT.MANAGE_MEMBERS')
              "
              variant="faded"
              size="xs"
              @click="toggleExpand(chat.id)"
            />
          </div>

          <!-- Section 2: Support members per group -->
          <div v-if="expandedChatId === chat.id" class="mt-3 pt-3 border-t border-n-slate-4">
            <SpinnerLoader v-if="loadingMembersChatId === chat.id" />
            <div v-else>
              <input
                type="text"
                :placeholder="$t('INBOX_MGMT.GROUPS_IMPORT.SEARCH_MEMBERS')"
                class="w-full mb-2 text-sm px-2 py-1 border border-n-slate-4 rounded"
                :value="memberSearch[chat.id] || ''"
                @input="setMemberSearch(chat.id, $event.target.value)"
              />
              <div class="max-h-64 overflow-y-auto flex flex-col gap-1">
                <div
                  v-for="m in filteredMembers(chat.id)"
                  :key="m.id"
                  class="flex items-center gap-2 py-1"
                >
                  <input
                    :id="`support-${chat.id}-${m.id}`"
                    type="checkbox"
                    :checked="isSupport(chat.id, m.id)"
                    @change="toggleSupport(chat.id, m.id)"
                  />
                  <label
                    :for="`support-${chat.id}-${m.id}`"
                    class="text-sm text-n-slate-12"
                  >
                    {{ memberLabel(m) }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex gap-2 mt-2">
          <NextButton
            :label="$t('INBOX_MGMT.GROUPS_IMPORT.SAVE_GROUPS')"
            :is-loading="isSavingMonitored"
            @click="saveMonitored"
          />
          <NextButton
            :label="$t('INBOX_MGMT.GROUPS_IMPORT.SAVE_SUPPORT')"
            variant="faded"
            :is-loading="isSavingSupport"
            @click="saveSupport"
          />
        </div>
      </div>
    </SettingsFieldSection>

    <!-- Section 3: Import history -->
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.GROUPS_IMPORT.IMPORT.TITLE')"
      :help-text="$t('INBOX_MGMT.GROUPS_IMPORT.IMPORT.HELP')"
    >
      <div class="flex flex-col gap-3">
        <select
          v-model.number="sinceDays"
          class="border border-n-slate-4 rounded px-2 py-1 text-sm w-60"
        >
          <option
            v-for="opt in sinceOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ $t(`INBOX_MGMT.GROUPS_IMPORT.SINCE.${opt.key}`) }}
          </option>
        </select>
        <NextButton
          :label="$t('INBOX_MGMT.GROUPS_IMPORT.START_IMPORT')"
          :is-loading="isImporting"
          :disabled="!selectedChatIds.length"
          @click="startImport"
        />
        <div
          v-if="!hasCaptainAssistant"
          class="text-xs text-n-slate-10"
        >
          {{ $t('INBOX_MGMT.GROUPS_IMPORT.NO_CAPTAIN_HINT') }}
        </div>
      </div>
    </SettingsFieldSection>

    <!-- Section 4: Progress -->
    <SettingsFieldSection
      v-if="statuses.length"
      :label="$t('INBOX_MGMT.GROUPS_IMPORT.PROGRESS.TITLE')"
    >
      <div class="flex flex-col gap-1 text-sm">
        <div
          v-for="s in statuses"
          :key="s.chat_id"
          class="flex justify-between border border-n-slate-4 rounded px-2 py-1"
        >
          <span class="text-n-slate-12">
            {{ chatTitle(s.chat_id) }}
          </span>
          <span class="text-n-slate-11">
            {{ s.imported || 0 }} msgs · {{ s.status }}
          </span>
        </div>
      </div>
    </SettingsFieldSection>
  </div>
</template>
